<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import type { RelatedEmployee, RelatedEmployeeCompanyFilterOption } from '~/api/related/employees'
import EmployeesSkeleton from './EmployeesSkeleton.vue'

const props = defineProps<{
  activeCompanyId: string
  companyFilterOptions: RelatedEmployeeCompanyFilterOption[]
  items: RelatedEmployee[]
  loading?: boolean
  total: number
}>()

const emit = defineEmits<{
  'change-company': [companyId: string]
  'employee-click': [employee: RelatedEmployee]
  'load-more': []
}>()

const { t } = useAppI18n()

const INITIAL_VISIBLE_COUNT = 6

/** 控制关联员工列表在首次加载后显示全部或前六项。 */
const isExpanded = ref(false)

/** 员工数据超出首屏数量时显示“查看更多”交互。 */
const hasExpandableEmployees = computed(() => props.total > INITIAL_VISIBLE_COUNT)

/** 单条员工记录使用 Figma 中的横向资料卡布局。 */
const isSingleEmployee = computed(() => props.total === 1)

/** 只有多家公司时增加“全部”并显示公司筛选条。 */
const shouldShowAllCompanies = computed(() => props.companyFilterOptions.length >= 2)

/** 单家公司无需额外筛选条，避免单条员工布局多出一行按钮。 */
const shouldShowCompanyFilters = computed(() => props.companyFilterOptions.length >= 2)

/** 根据展开状态截取员工列表，避免首屏展示过长。 */
const visibleEmployees = computed(() => (isExpanded.value ? props.items : props.items.slice(0, INITIAL_VISIBLE_COUNT)))

/** 保存每张员工卡片的职位信息容器，用于判断完整职位标签是否仍在可视范围内。 */
const employeeMetaElements = new Map<string, HTMLElement>()

/** 监听卡片宽度变化，在布局变化后重新隐藏放不下的完整职位标签。 */
let positionVisibilityObserver: ResizeObserver | undefined

/** 合并同一帧内的多次重新计算，避免列表渲染时重复测量。 */
let positionVisibilityFrame = 0

/** 重新计算职位标签的可见性，溢出时从首个放不下的标签起全部隐藏。 */
const updatePositionVisibility = () => {
  positionVisibilityFrame = 0

  employeeMetaElements.forEach((metaElement) => {
    const positionChips = Array.from(metaElement.querySelectorAll<HTMLElement>('[data-position-chip]'))
    const metaRight = metaElement.getBoundingClientRect().right
    let shouldHideRemainingChips = false

    positionChips.forEach((positionChip) => {
      positionChip.hidden = false
    })

    positionChips.forEach((positionChip) => {
      if (shouldHideRemainingChips || positionChip.getBoundingClientRect().right > metaRight) {
        positionChip.hidden = true
        shouldHideRemainingChips = true
      }
    })
  })
}

/** 在 DOM 更新完成后的下一帧统一计算职位标签是否完整可见。 */
const schedulePositionVisibilityUpdate = async () => {
  if (typeof window === 'undefined') {
    return
  }

  await nextTick()

  if (positionVisibilityFrame) {
    cancelAnimationFrame(positionVisibilityFrame)
  }

  positionVisibilityFrame = requestAnimationFrame(updatePositionVisibility)
}

/** 记录员工职位信息容器，并在新增或移除时同步调整尺寸监听。 */
const setEmployeeMetaElement = (employeeKey: string, element: Element | null) => {
  const previousElement = employeeMetaElements.get(employeeKey)

  if (previousElement && previousElement !== element) {
    positionVisibilityObserver?.unobserve(previousElement)
  }

  if (element instanceof HTMLElement) {
    employeeMetaElements.set(employeeKey, element)
    positionVisibilityObserver?.observe(element)
  } else {
    employeeMetaElements.delete(employeeKey)
  }

  schedulePositionVisibilityUpdate()
}

/** 同一员工可能在多家公司任职，索引兼容同公司多条任职或缺少员工 ID 的记录。 */
const getEmployeeKey = (employee: RelatedEmployee, index: number) =>
  `${employee.companyId || 'company'}-${employee.officerId || employee.name || 'employee'}-${index}`

/** 切换企业分组，并交由父级重新请求对应员工数据。 */
const handleCompanyFilterClick = (companyId: string) => {
  if (companyId === props.activeCompanyId && (props.loading || props.items.length > 0)) {
    return
  }

  emit('change-company', companyId)
}

/** 展开员工列表时请求剩余数据，再次点击收起至首屏数量。 */
const handleLoadMore = () => {
  if (!hasExpandableEmployees.value || props.loading) {
    return
  }

  isExpanded.value = !isExpanded.value

  if (isExpanded.value) {
    emit('load-more')
  }
}

/** 员工卡片点击后由父级打开 WikiBit APP 下载引导。 */
const handleEmployeeClick = (employee: RelatedEmployee) => {
  emit('employee-click', employee)
}

watch(
  () => props.activeCompanyId,
  () => {
    isExpanded.value = false
  },
)

watch([visibleEmployees, () => props.loading], schedulePositionVisibilityUpdate, { flush: 'post' })

onMounted(() => {
  positionVisibilityObserver = new ResizeObserver(schedulePositionVisibilityUpdate)
  employeeMetaElements.forEach((metaElement) => positionVisibilityObserver?.observe(metaElement))
  schedulePositionVisibilityUpdate()
})

onBeforeUnmount(() => {
  positionVisibilityObserver?.disconnect()

  if (positionVisibilityFrame) {
    cancelAnimationFrame(positionVisibilityFrame)
  }
})
</script>

<template>
  <section class="w-full" :class="{ 'pt-[16px]': shouldShowCompanyFilters }" :aria-busy="loading">
    <div
      v-if="shouldShowCompanyFilters"
      class="flex w-full items-center overflow-x-auto px-[0px] py-[0px]"
      role="tablist"
      :aria-label="t('82674', [''])"
    >
      <div class="flex min-w-max items-center gap-[8px]">
        <button
          v-if="shouldShowAllCompanies"
          type="button"
          class="flex h-[28px] cursor-pointer items-center rounded-[2px] border-[0.5px] px-[11.5px] py-[4px] text-[13px] leading-[20px] font-[500]"
          :class="
            activeCompanyId
              ? 'border-[rgba(11,18,40,0.14)] bg-white text-[#292A2E] hover:bg-[#F8F8F8]'
              : 'border-[#112D73] bg-[#112D73] text-white'
          "
          :aria-selected="!activeCompanyId"
          role="tab"
          @click="handleCompanyFilterClick('')"
        >
          {{ t('83794') }}
        </button>
        <button
          v-for="company in companyFilterOptions"
          :key="company.companyId || company.companyName"
          type="button"
          class="flex h-[28px] max-w-[280px] cursor-pointer items-center gap-[8px] rounded-[2px] border-[0.5px] px-[12px] py-[4px] text-[13px] leading-[20px] transition-colors"
          :class="
            company.companyId === activeCompanyId
              ? 'border-[#112D73] bg-[#112D73] text-white'
              : 'border-[rgba(11,18,40,0.14)] bg-white text-[#292A2E] hover:bg-[#F8F8F8]'
          "
          :aria-selected="company.companyId === activeCompanyId"
          :title="company.companyName || ''"
          role="tab"
          @click="handleCompanyFilterClick(company.companyId || '')"
        >
          <CommonImage
            :src="company.companyIco || ''"
            :alt="company.companyName || ''"
            width="16"
            height="16"
            loading="lazy"
            class="size-[16px] shrink-0 rounded-[2px] object-cover"
          />
          <span class="truncate">{{ company.companyName || '--' }}</span>
        </button>
      </div>
    </div>

    <EmployeesSkeleton v-if="loading" :count="INITIAL_VISIBLE_COUNT" />

    <div v-else-if="items.length > 0" class="pt-[20px]">
      <div
        class="grid grid-cols-1 gap-x-[24px] gap-y-[24px]"
        :class="isSingleEmployee ? 'grid-cols-1' : 'min-[800px]:grid-cols-2 min-[1200px]:grid-cols-3'"
      >
        <article
          v-for="(employee, employeeIndex) in visibleEmployees"
          :key="getEmployeeKey(employee, employeeIndex)"
          class="flex min-w-0 cursor-pointer flex-col gap-[12px] rounded-[8px] hover:bg-[#f8f8f8] hover:shadow-[0_0_0_4px_#f8f8f8]"
          role="button"
          tabindex="0"
          @click="handleEmployeeClick(employee)"
          @keydown.enter="handleEmployeeClick(employee)"
          @keydown.space.prevent="handleEmployeeClick(employee)"
        >
          <div class="flex min-w-0 items-start gap-[12px]">
            <div class="relative size-[40px] shrink-0">
              <CommonImage
                :src="employee.logo || ''"
                :alt="employee.name || 'employee'"
                width="40"
                height="40"
                loading="lazy"
                class="size-[40px] rounded-full object-cover"
              />
              <CommonImage
                v-if="employee.countryCircleFlag || employee.countryFlag"
                :src="employee.countryCircleFlag || employee.countryFlag || ''"
                alt=""
                width="16"
                height="16"
                loading="lazy"
                class="absolute end-[-1px] bottom-[-1px] size-[16px] rounded-full object-cover"
                aria-hidden="true"
              />
            </div>
            <div class="min-w-0 flex-1">
              <h4 class="m-[0px] truncate text-[16px] leading-[24px] font-[500] text-[#292A2E]">
                {{ employee.name || '--' }}
              </h4>
              <div
                :ref="(element: Element | null) => setEmployeeMetaElement(getEmployeeKey(employee, employeeIndex), element)"
                class="mt-[2px] flex min-w-0 items-center gap-[8px] text-[12px] leading-[18px] text-[#505258]"
                :class="isSingleEmployee ? 'flex-wrap' : 'flex-nowrap overflow-hidden'"
              >
                <span v-if="employee.countryName" class="shrink-0">{{ employee.countryName }}</span>
                <span
                  v-if="employee.countryName && employee.positionNames?.length"
                  class="h-[8px] w-[1px] shrink-0 bg-[rgba(11,18,40,0.14)]"
                  aria-hidden="true"
                ></span>
                <span
                  v-for="positionName in employee.positionNames || []"
                  :key="positionName"
                  class="shrink-0"
                  :class="isSingleEmployee ? '' : 'max-w-full truncate rounded-[2px] bg-transparent px-[4px]'"
                  data-position-chip
                >
                  {{ positionName }}
                </span>
              </div>
            </div>
          </div>

          <div
            class="flex min-w-0 flex-col gap-[12px] rounded-[8px] border border-[rgba(5,21,36,0.06)] bg-white p-[16px]"
            :class="isSingleEmployee ? 'h-[96px]' : 'h-[120px]'"
          >
            <div
              class="border-b-[0.5px] border-[rgba(5,21,36,0.06)] pb-[12px]"
              :class="isSingleEmployee ? 'flex items-center gap-[12px]' : 'grid grid-cols-2 gap-[4px]'"
            >
              <div class="min-w-0" :class="{ 'flex flex-1 items-start gap-[8px]': isSingleEmployee }">
                <p
                  class="m-[0px] truncate text-[#7D818A]"
                  :class="isSingleEmployee ? 'text-[14px] leading-[22px]' : 'text-[13px] leading-[20px]'"
                >
                  {{ t('89176') }}
                </p>
                <p
                  class="m-[0px] truncate text-[14px] leading-[22px] font-[500] text-[#292A2E]"
                  :class="{ 'mt-[4px]': !isSingleEmployee }"
                >
                  {{ employee.startDate || '--' }}
                </p>
              </div>
              <div class="min-w-0" :class="{ 'flex flex-1 items-start gap-[8px]': isSingleEmployee }">
                <p
                  class="m-[0px] truncate text-[#7D818A]"
                  :class="isSingleEmployee ? 'text-[14px] leading-[22px]' : 'text-[13px] leading-[20px]'"
                >
                  {{ t('80340') }}
                </p>
                <p
                  class="m-[0px] truncate text-[14px] leading-[22px] font-[500] text-[#292A2E]"
                  :class="{ 'mt-[4px]': !isSingleEmployee }"
                >
                  {{ employee.officerStatus || '--' }}
                </p>
              </div>
            </div>
            <div class="flex min-w-0 items-center gap-[8px]">
              <CommonImage
                v-if="employee.companyIco || employee.companyLogo"
                :src="employee.companyIco || employee.companyLogo || ''"
                :alt="employee.companyName || ''"
                width="16"
                height="16"
                loading="lazy"
                class="size-[16px] shrink-0 rounded-[2px] object-cover"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1 truncate text-[12px] leading-[18px] text-[#505258]">
                {{ employee.companyName || '--' }}
              </span>
            </div>
          </div>
        </article>
      </div>

      <button
        v-if="hasExpandableEmployees"
        type="button"
        class="flex h-[52px] w-full cursor-pointer items-center justify-center bg-transparent py-[16px] text-[13px] leading-[20px] text-[#0D3F63] transition-colors hover:text-[#112D73] disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="loading"
        :aria-busy="loading"
        @click="handleLoadMore"
      >
        {{ isExpanded ? t('83802') : t('84570') }}
      </button>
    </div>

    <div v-else class="py-[32px] text-center text-[14px] leading-[22px] text-[#7D818A]">--</div>
  </section>
</template>
